import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert } from 'src/ui/Alert';
import { Card } from 'src/ui/Card';
import { Spinner } from 'src/ui/Spinner';

/**
 * Google Identity Services web client (PUBLIC value — a client ID is not a
 * secret). Defaults to the `lovellect-app` project's Web client, the same one
 * Android sign-in uses as its webClientId, which serves the deployed
 * dashboards. Local development overrides it via REACT_APP_GOOGLE_CLIENT_ID,
 * because localhost is registered on its own client rather than on the one
 * production depends on.
 *
 * Whichever client is in play, two things have to line up or sign-in fails:
 * its "Authorized JavaScript origins" must list the origin the dashboard is
 * served from (GSI rejects a mismatch with `origin_mismatch` before any
 * request reaches us), and the server's GOOGLE_OAUTH_CLIENT_IDS must list the
 * client ID itself, since that is the audience it verifies the token against.
 */
const GOOGLE_WEB_CLIENT_ID =
  process.env.REACT_APP_GOOGLE_CLIENT_ID ||
  '1098344636170-qlsh046gh526oldclv1fh7utpmk5fcvq.apps.googleusercontent.com';

const GSI_SRC = 'https://accounts.google.com/gsi/client';

interface GoogleCredentialResponse {
  credential: string;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: GoogleCredentialResponse) => void;
          }) => void;
          renderButton: (
            parent: HTMLElement,
            options: Record<string, unknown>
          ) => void;
        };
      };
    };
  }
}

interface LoginPageProps {
  /** Called with the Google ID token once the user picks an account. */
  onCredential: (idToken: string) => Promise<void>;
  /** Signed in fine, but the account has no admin role. */
  accessDenied?: boolean;
}

const LoginPage = ({ onCredential, accessDenied }: LoginPageProps) => {
  const buttonHost = useRef<HTMLDivElement>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const handleCredential = useCallback(
    async (response: GoogleCredentialResponse) => {
      setBusy(true);
      setError('');
      try {
        await onCredential(response.credential);
      } catch (e) {
        const message = (e as { data?: { message?: string } })?.data?.message;
        setError(message || 'Sign-in failed. Please try again.');
      } finally {
        setBusy(false);
      }
    },
    [onCredential]
  );

  useEffect(() => {
    const renderButton = () => {
      if (!window.google || !buttonHost.current) {
        return;
      }
      window.google.accounts.id.initialize({
        client_id: GOOGLE_WEB_CLIENT_ID,
        callback: handleCredential,
      });
      buttonHost.current.innerHTML = '';
      window.google.accounts.id.renderButton(buttonHost.current, {
        theme: 'outline',
        size: 'large',
        width: 280,
        text: 'signin_with',
      });
    };

    if (window.google) {
      renderButton();
      return;
    }
    let script = document.querySelector<HTMLScriptElement>(
      `script[src="${GSI_SRC}"]`
    );
    if (!script) {
      script = document.createElement('script');
      script.src = GSI_SRC;
      script.async = true;
      document.head.appendChild(script);
    }
    script.addEventListener('load', renderButton);
    return () => script?.removeEventListener('load', renderButton);
  }, [handleCredential]);

  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        width: '100vw',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg-app)',
      }}
    >
      <Card
        pad
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 16,
          minWidth: 360,
          padding: '40px 48px',
        }}
      >
        <div
          style={{
            fontSize: 20,
            fontWeight: 700,
            color: 'var(--text)',
          }}
        >
          恋 Ai 脑 · Dashboard
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          Sign in with your admin Google account
        </div>
        {accessDenied && (
          <Alert color="amber">
            This account has no dashboard access. Ask an admin to grant your
            account a role, then sign in again.
          </Alert>
        )}
        {error && <Alert color="red">{error}</Alert>}
        {busy ? <Spinner size="lg" /> : <div ref={buttonHost} />}
      </Card>
    </div>
  );
};

export default LoginPage;
